import { Alert, useToastProvider } from "@deere/fuel-react";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  DataGridPro,
  gridClasses,
  GridColDef,
  gridFilteredTopLevelRowCountSelector,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowModel,
  GridRowsProp,
  useGridApiRef,
} from "@mui/x-data-grid-pro";

import { useOktaAuth } from "@okta/okta-react";

import { parse } from "papaparse";
import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import EntryLinesToolbar from "./components/EntryLinesToolbar/EntryLinesToolbar";
import { CsvRowType } from "./types";
import {
  D2DInventoryApi,
  D2DInventoryItem,
} from "../../../../../api/d2dInventory";
import {
  LineValidationRequest,
  LineValidationResponse,
} from "../../../../../api/validation/validationInterfaces";
import useXtremeBffApi from "../../../../../api/xtremeBff/useXtremeBff";
import xtremeBffApi from "../../../../../api/xtremeBff/xtremeBffApi";
import LoadingIndicator from "../../../../../components/LoadingIndicator/LoadingIndicator";
import StickyFooter from "../../../../../components/StickyFooter/StickyFooter";
import { ConfigContext } from "../../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../../context/DealerAccountContext";
import OrderLinesPagination from "../../../../orders/components/ReviewLinesPagination/OrderLinesPagination";
import { LineState } from "../../../../orders/types";
import D2DFileUploadModal from "../D2DFileUploadModal/D2DFileUploadModal";
import ProcessSuccess from "../ProcessSuccess/ProcessSuccess";

const DEFAULT_ENTRY_LINES_COUNT = 10;
const MAX_ORDER_LINES = 999;
const PAGE_SIZE = 50;

interface LineValidationState {
  isQuantityValid: boolean;
  quantityFailureMessage: string;
  isPartNumberValid: boolean;
  partNumberFailureMessage: string;
  showPartNumberInfo: boolean;
  isValidated?: boolean;
}

interface EntryLine {
  id: string;
  partNumber: string;
  quantity: number;
  bin: string;
  action: boolean;
  isEdited: boolean;
  isNew?: boolean;
  state: LineState;
  showQuantityWarning: boolean;
  quantityWarningMessage: string;
  showPartNumberWarning: boolean;
  partNumberWarningMessage: string;
  showPartNumberInfo: boolean;
  isValidated: boolean;
  uniqueLineId?: string;
}

interface EntryLinesProps {
  onSuccessConfirmation?: (show: boolean) => void;
}

const EntryLines: React.FC<EntryLinesProps> = ({ onSuccessConfirmation }) => {
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const { oktaAuth, authState } = useOktaAuth();
  const {
    D2D_INVENTORY_API_URL,
    XTREME_BFF_SERVICE_URL,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_VALIDATIONS_SERVICE,
  } = useContext(ConfigContext);
  const { dealerAccount } = useContext(DealerAccountContext);
  const { t } = useTranslation(["d2d", "orders", "common"]);
  const { enqueueToast } = useToastProvider();
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showCancelOrderModal, setShowCancelOrderModal] = useState(false);
  const [isProcessChangesButtonDisabled, setIsProcessChangesButtonDisabled] =
    useState(false);
  const [uploadedFileCount, setUploadedFileCount] = useState<number>(0);

  const getAccessToken = (): string => {
    const authToken = oktaAuth.getAccessToken();
    if (!authToken) return "";
    return authToken;
  };
  const xtremeBffApiHook = useXtremeBffApi();
  const isValidationServiceApiAuthEnable =
    ENABLE_RBAC_AUTH_SUPPORT &&
    ENABLE_RBAC_AUTH_SUPPORT_FOR_VALIDATIONS_SERVICE;

  const parseLineValidationResponse = (
    response: LineValidationResponse
  ): LineValidationState => {
    if (response.status === "SUCCESS") {
      return {
        isQuantityValid: true,
        quantityFailureMessage: "",
        isPartNumberValid: true,
        partNumberFailureMessage: "",
        showPartNumberInfo: false,
        isValidated: true,
      };
    }

    // Find DEALER_INPUT_VALIDATOR specifically
    const dealerInputValidator = response.validators?.find(
      (validator) => validator.validatorName === "DEALER_INPUT_VALIDATOR"
    );

    if (!dealerInputValidator) {
      return {
        isQuantityValid: true,
        quantityFailureMessage: "",
        isPartNumberValid: true,
        partNumberFailureMessage: "",
        showPartNumberInfo: false,
        isValidated: true,
      };
    }

    const validations =
      dealerInputValidator.validationReport?.validations || [];

    // Check PART_NUMBER_VALIDATION only
    const partNumberValidation = validations.find(
      (validation) =>
        validation.category === "PART_NUMBER_VALIDATION" &&
        validation.status === "FAILURE"
    );

    let partNumberFailureMessage = "";
    const showPartNumberInfo = false;

    if (partNumberValidation) {
      partNumberFailureMessage = "d2d:myLocatorEntry.partNumber.required";
    }

    return {
      isQuantityValid: true,
      quantityFailureMessage: "",
      isPartNumberValid:
        !partNumberValidation || partNumberValidation.status === "SUCCESS",
      partNumberFailureMessage,
      showPartNumberInfo,
      isValidated: true,
    };
  };

  const lineValidation = (
    partNumber: string,
    quantity: number,
    accountId: string,
    countryCode: string,
    token: string
  ): Promise<LineValidationState> => {
    const payload: LineValidationRequest = {
      accountId,
      partNumber,
      requestedQuantity: `${quantity}`,
      countryCode,
    };
    return (
      isValidationServiceApiAuthEnable
        ? xtremeBffApiHook.lineValidation(XTREME_BFF_SERVICE_URL, payload)
        : xtremeBffApi.lineValidation(XTREME_BFF_SERVICE_URL, payload, token)
    )
      .then((response) => parseLineValidationResponse(response))
      .catch(() => ({
        isQuantityValid: true,
        quantityFailureMessage: "",
        isPartNumberValid: true,
        partNumberFailureMessage: "",
        isValidated: false,
        showPartNumberInfo: false,
      }));
  };

  const processLineWithValidation = (
    partNumber: string,
    quantity: number,
    bin: string
  ): Promise<Omit<EntryLine, "id">> =>
    lineValidation(
      partNumber,
      quantity,
      dealerAccount?.id || "",
      dealerAccount?.location?.country || "US",
      getAccessToken()
    ).then((response) => ({
      partNumber,
      quantity,
      bin,
      action: false,
      isNew: false,
      isEdited: true,
      state: LineState.VALIDATED,
      showQuantityWarning: !response.isQuantityValid,
      quantityWarningMessage: response.quantityFailureMessage,
      showPartNumberWarning: !response.isPartNumberValid,
      partNumberWarningMessage: response.partNumberFailureMessage,
      showPartNumberInfo: response.showPartNumberInfo,
      isValidated: true,
    }));

  const linesValidation = async (
    importLines: { partNumber: string; quantity: number; bin: string }[],
    countryCode: string,
    token: string
  ): Promise<EntryLine[]> => {
    const payload: LineValidationRequest[] = importLines.map((line) => ({
      accountId: dealerAccount?.id || "",
      partNumber: line.partNumber,
      requestedQuantity: `${line.quantity}`,
      countryCode,
    }));

    try {
      const responses = isValidationServiceApiAuthEnable
        ? await xtremeBffApiHook.linesValidation(
            XTREME_BFF_SERVICE_URL,
            payload
          )
        : await xtremeBffApi.linesValidation(
            XTREME_BFF_SERVICE_URL,
            payload,
            token,
            false
          );
      return importLines.map((importLine) => {
        const lineResponse: LineValidationResponse =
          responses[`${importLine.partNumber}-${importLine.quantity}`];
        const lineValidationState: LineValidationState =
          parseLineValidationResponse(lineResponse);
        return {
          id: crypto.randomUUID(),
          quantity: importLine.quantity,
          showQuantityWarning: !lineValidationState.isQuantityValid,
          quantityWarningMessage: lineValidationState.quantityFailureMessage,
          partNumber: importLine.partNumber,
          showPartNumberWarning: !lineValidationState.isPartNumberValid,
          partNumberWarningMessage:
            lineValidationState.partNumberFailureMessage,
          showPartNumberInfo: lineValidationState.showPartNumberInfo,
          state: LineState.VALIDATED,
          bin: importLine.bin,
          action: false,
          isEdited: true,
          isNew: false,
          isValidated: true,
        };
      });
    } catch {
      return importLines.map((importLine) => ({
        id: crypto.randomUUID(),
        quantity: importLine.quantity,
        showQuantityWarning: importLine.quantity <= 0,
        quantityWarningMessage:
          importLine.quantity <= 0 ? "d2d:myLocatorEntry.quantity.invalid" : "",
        partNumber: importLine.partNumber,
        showPartNumberWarning: !importLine.partNumber,
        partNumberWarningMessage: !importLine.partNumber
          ? "d2d:myLocatorEntry.partNumber.invalid"
          : "",
        state: LineState.VALIDATED,
        showPartNumberInfo: false,
        bin: importLine.bin,
        action: false,
        isEdited: true,
        isNew: false,
        isValidated: true,
      }));
    }
  };

  const processLines = async (
    entryLines: { partNumber: string; quantity: number; bin: string }[]
  ): Promise<EntryLine[]> =>
    linesValidation(
      entryLines,
      dealerAccount?.location?.country || "US",
      getAccessToken()
    );

  const filterLinesWithSuccessfulValidation = (
    rows: EntryLine[]
  ): EntryLine[] =>
    rows.filter(
      (row) =>
        !row.showQuantityWarning &&
        (!row.showPartNumberWarning || row.showPartNumberInfo) &&
        row.isValidated
    );

  const getUserId = (): string =>
    (authState?.accessToken?.claims?.userID as string) || "";

  const getAccountId = (): string => dealerAccount?.id || "";

  const MemoizedHeader = React.memo(({ title }: { title: string }) => (
    <Typography sx={columnsHeaderSx}>{title}</Typography>
  ));

  const MemoizedLinkButton = React.memo(({ url }: { url: string }) => (
    <Link
      to={url}
      style={{
        color: theme.tokens.FuelColorBrandPrimary,
      }}
      target="_blank"
      color={theme.tokens.FuelColorBorderLight}
      data-testid="partNumber-link"
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation();
      }}
    >
      <OpenInNewIcon
        fontSize="small"
        data-testid="openInNewIcon-enabled"
        sx={{
          position: "relative",
          left: theme.tokens.FuelSpacing1,
          margin: theme.tokens.FuelBorderRadiusLg,
        }}
      />
    </Link>
  ));

  const columns: GridColDef[] = [
    {
      field: "lines",
      renderHeader: () => (
        <MemoizedHeader
          title={t("d2d:myLocatorEntry.table.columnHeader.lines")}
        />
      ),
      renderCell: (params: GridRenderCellParams) => {
        const rowIndex = visibleRows.findIndex((row) => row.id === params.id);
        return <Typography>{rowIndex + 1}</Typography>;
      },
      flex: 0.3,
      sortable: false,
      editable: false,
      headerAlign: "left",
      align: "left",
      minWidth: 50,
    },
    {
      field: "partNumber",
      renderHeader: () => (
        <MemoizedHeader
          title={t("d2d:myLocatorEntry.table.columnHeader.partNumber")}
        />
      ),
      renderCell: (params: GridRenderCellParams) => {
        const url = `/dealer/parts/${encodeURIComponent(params.value)}`;
        return (
          <>
            <TextField
              id="partNumber-render"
              placeholder={t("d2d:input.placeholder") || "Enter input here..."}
              type="text"
              size="large"
              value={params.value}
              inputProps={{ maxLength: 30 }}
              helperText={t(params.row.partNumberWarningMessage)}
              error={
                params.row.partNumberWarningMessage !== "" &&
                !params.row.showPartNumberInfo
              }
            />
            {params.value &&
              (!params.row.showPartNumberWarning ||
                params.row.showPartNumberInfo) && (
                <MemoizedLinkButton url={url} />
              )}
          </>
        );
      },
      renderEditCell: (params: GridRenderEditCellParams) => (
        <TextField
          id="partNumber-edit"
          placeholder={t("d2d:input.placeholder") || "Enter input here..."}
          type="text"
          size="large"
          value={params.value}
          inputProps={{ maxLength: 30 }}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value.replace(/[^a-zA-Z0-9\-/]/g, "");
            apiRef.current.setEditCellValue({
              id: params.id,
              field: params.field,
              value: newValue,
            });
          }}
        />
      ),
      flex: 1,
      sortable: false,
      editable: true,
      headerAlign: "left",
      align: "left",
      minWidth: 120,
    },
    {
      field: "quantity",
      renderHeader: () => (
        <MemoizedHeader
          title={t("d2d:myLocatorEntry.table.columnHeader.quantity")}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <TextField
          id="quantity-render"
          placeholder={t("d2d:input.placeholder") || "Enter input here..."}
          type="text"
          size="large"
          value={params.value || ""}
          error={params.row.quantityWarningMessage !== ""}
          helperText={t(params.row.quantityWarningMessage)}
        />
      ),
      renderEditCell: (params: GridRenderEditCellParams) => (
        <TextField
          id="quantity-edit"
          placeholder={t("d2d:input.placeholder") || "Enter input here..."}
          type="text"
          size="large"
          value={params.value || ""}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const inputValue = event.target.value;
            const isValidInput =
              inputValue === "" ||
              (!Number.isNaN(Number(inputValue)) &&
                Number(inputValue) >= 1 &&
                Number(inputValue) <= 999999);

            if (isValidInput) {
              apiRef.current.setEditCellValue({
                id: params.id,
                field: params.field,
                value: inputValue === "" ? "" : Number(inputValue),
              });
            }
          }}
        />
      ),
      flex: 0.6,
      sortable: false,
      editable: true,
      headerAlign: "left",
      align: "left",
      minWidth: 100,
    },
  ];

  const createEntryLines = (count: number): EntryLine[] =>
    Array.from({ length: count }).map(() => ({
      id: crypto.randomUUID(),
      partNumber: "",
      quantity: 0,
      bin: "",
      action: true,
      isEdited: false,
      isNew: true,
      state: LineState.CREATED,
      showQuantityWarning: false,
      quantityWarningMessage: "",
      showPartNumberWarning: false,
      partNumberWarningMessage: "",
      showPartNumberInfo: false,
      isValidated: false,
    }));

  const [rows, setRows] = useState<EntryLine[]>(
    createEntryLines(DEFAULT_ENTRY_LINES_COUNT)
  );
  const [uploadedRows, setUploadedRows] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<GridRowsProp[]>([]);
  const [showContinueToNextWarningModal, setShowContinueToNextWarningModal] =
    useState<boolean>(false);
  const [showFileUploadModal, setShowFileUploadModal] =
    useState<boolean>(false);
  const [noValidLines, setNoValidLines] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadingIndicator, setLoadingIndicator] = useState<boolean>(false);
  const [isResetButtonDisabled, setIsResetButtonDisabled] =
    useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [uniqueRows, setUniqueRows] = useState<number>(rows.length);
  const [isFileUploaded, setIsFileUploaded] = useState<boolean>(false);
  const [showSuccessConfirmation, setShowSuccessConfirmation] =
    useState<boolean>(false);
  const [processedLineCount, setProcessedLineCount] = useState<number>(0);
  const [processedFileCount, setProcessedFileCount] = useState<number>(0);
  const enteredRowCount = () =>
    rows.filter((row) => row.partNumber && row.quantity).length;

  useEffect(() => {
    if (!rows.find((row) => row.partNumber && row.quantity)) {
      setIsResetButtonDisabled(true);
    } else {
      setIsResetButtonDisabled(false);
    }

    if (rows.length < page * pageSize + 1) {
      setPage(0);
    }
  }, [rows, page, pageSize]);

  useEffect(() => {
    async function processUploadedRows() {
      try {
        setLoadingIndicator(true);
        const processedLine = await processLines(uploadedRows);
        setRows((prevRows) => {
          const existingLines = prevRows.filter(
            (prevRow) => prevRow.partNumber || prevRow.quantity
          );
          const extraLines = createEntryLines(
            DEFAULT_ENTRY_LINES_COUNT -
              (processedLine.length +
                (isFileUploaded ? existingLines.length : 0))
          );
          if (uploadedRows.length > 0 && !isFileUploaded) {
            return [...processedLine, ...extraLines];
          }
          return [...processedLine, ...existingLines, ...extraLines];
        });
      } finally {
        setLoadingIndicator(false);
      }
    }

    if (uploadedRows.length > 0) {
      processUploadedRows().then();
    }
  }, [uploadedRows]);

  const updateRow = (newRow: EntryLine, updatedRow: EntryLine) => {
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === newRow.id ? updatedRow : row))
    );
  };

  const processRowUpdate = (newRow: EntryLine) => {
    let editedRow = newRow;
    // @ts-ignore
    if (editedRow.quantity === "") {
      editedRow = { ...editedRow, quantity: 0 };
    }

    const existingRow = rows.find((row) => row.id === editedRow.id);
    if (
      existingRow &&
      editedRow.partNumber === existingRow.partNumber &&
      editedRow.quantity === existingRow.quantity &&
      editedRow.bin === existingRow.bin
    ) {
      return existingRow;
    }

    if (
      editedRow.partNumber === "" &&
      editedRow.quantity === 0 &&
      editedRow.bin !== ""
    ) {
      const updatedRow: EntryLine = {
        ...editedRow,
        action: false,
        isEdited: true,
        isNew: false,
        state: LineState.VALIDATED,
        showQuantityWarning: true,
        quantityWarningMessage: "d2d:myLocatorEntry.quantity.invalid",
        showPartNumberWarning: true,
        partNumberWarningMessage: "d2d:myLocatorEntry.partNumber.required",
        showPartNumberInfo: false,
        isValidated: true,
      };
      updateRow(editedRow, updatedRow);
      return updatedRow;
    }

    if (
      existingRow &&
      editedRow.partNumber === existingRow.partNumber &&
      editedRow.quantity === existingRow.quantity &&
      editedRow.bin !== existingRow.bin
    ) {
      updateRow(editedRow, { ...existingRow, bin: editedRow.bin });
      return { ...existingRow, bin: editedRow.bin };
    }

    if (editedRow.partNumber === "" && editedRow.quantity > 0) {
      const updatedRow: EntryLine = {
        ...editedRow,
        action: false,
        isEdited: true,
        isNew: false,
        state: LineState.VALIDATED,
        showQuantityWarning: false,
        quantityWarningMessage: "",
        showPartNumberWarning: true,
        partNumberWarningMessage: "d2d:myLocatorEntry.partNumber.required",
        showPartNumberInfo: false,
        isValidated: true,
      };
      updateRow(editedRow, updatedRow);
      return updatedRow;
    }

    let updatedRow: EntryLine;

    if (editedRow.partNumber || editedRow.quantity || editedRow.bin) {
      return processLineWithValidation(
        editedRow.partNumber,
        editedRow.quantity,
        editedRow.bin
      ).then((validatedLine) => {
        updatedRow = { ...validatedLine, id: editedRow.id };
        updateRow(editedRow, updatedRow);
        return updatedRow;
      });
    }
    updatedRow = {
      ...editedRow,
      action: true,
      isEdited: false,
      isNew: false,
      state: LineState.VALIDATED,
      showQuantityWarning: false,
      quantityWarningMessage: "",
      showPartNumberWarning: false,
      partNumberWarningMessage: "",
      showPartNumberInfo: false,
      isValidated: false,
    };
    updateRow(editedRow, updatedRow);
    return updatedRow;
  };

  const onRowsSelectionHandler = (ids: any) => {
    const selectedRowsData = ids.map((id: string) =>
      rows.find((row) => row.id === id)
    );
    setSelectedRow(selectedRowsData);
  };

  const addRows = (count: number) => {
    const createdLines = createEntryLines(count);
    setRows((prevRows) => [...prevRows, ...createdLines]);

    setTimeout(() => {
      let lastPage = Math.ceil(rows.length / pageSize) - 1;
      const lastFilledRow = (rows.length + count) % pageSize === 0;
      if (lastFilledRow) lastPage += 1;
      setPage(lastPage);
      apiRef.current.setPage(lastPage);
      apiRef.current.setPaginationModel({
        pageSize,
        page: lastPage,
      });
    }, 100);
  };

  const resetButtonCallback = () => {
    setShowRowsWithIssuesOnly(false);
    setRows(createEntryLines(DEFAULT_ENTRY_LINES_COUNT));
    setPage(0);
  };

  const parseFiles = (files: File[]) => {
    if (files && files.length > 0) {
      const parsePromises: Promise<any[]>[] = [];

      files.forEach((file) => {
        const fileType = file.name.split(".").slice(-1);
        if (file && fileType.includes("csv")) {
          const parsePromise = new Promise<any[]>((resolve) => {
            parse<CsvRowType>(file, {
              header: true,
              skipEmptyLines: true,
              complete(results) {
                if (
                  results.meta.fields !== undefined &&
                  (results.meta.fields.length > 2 ||
                    !results.meta.fields.includes("Quantity") ||
                    !results.meta.fields.includes("PartNumber"))
                ) {
                  enqueueToast({
                    variant: "error",
                    message: t("d2d:myLocatorEntry.importLines.incorrectData"),
                    isCloseable: true,
                  });
                } else {
                  const isNotEmpty = (value: string | undefined) =>
                    value && value.trim();

                  const isNumber = (value: string) =>
                    !Number.isNaN(Number(value.trim()));

                  const filterEmptyRows = results.data.filter(
                    (fileRows) =>
                      isNotEmpty(fileRows.PartNumber) ||
                      (isNotEmpty(fileRows.Quantity) &&
                        isNumber(fileRows.Quantity))
                  );
                  const trimmedValue = (value: string | undefined) =>
                    value !== undefined ? value.trim() : "";

                  const filteredData = filterEmptyRows.map((row) => ({
                    partNumber: trimmedValue(row.PartNumber).toUpperCase(),
                    quantity: Number(trimmedValue(row.Quantity)),
                    bin: "",
                  }));

                  const editedRowLength = rows.filter(
                    (row) => row.partNumber && row.quantity
                  ).length;
                  const sum = editedRowLength + filteredData.length;

                  if (filteredData.length > MAX_ORDER_LINES) {
                    enqueueToast({
                      variant: "error",
                      message: t(
                        "d2d:myLocatorEntry.importLines.exceed.numberOfLines"
                      ),
                      isCloseable: true,
                    });
                  } else if (sum > MAX_ORDER_LINES) {
                    enqueueToast({
                      variant: "error",
                      message: t(
                        "d2d:myLocatorEntry.totalLines.exceed.numberOfLines",
                        {
                          remainingLines: MAX_ORDER_LINES - editedRowLength,
                        }
                      ),
                      isCloseable: true,
                    });
                  } else if (
                    filteredData.length > 0 &&
                    filteredData.length <= MAX_ORDER_LINES &&
                    sum <= MAX_ORDER_LINES
                  ) {
                    setIsFileUploaded(true);
                    resolve(filteredData);
                  } else {
                    enqueueToast({
                      variant: "error",
                      message: t("d2d:myLocatorEntry.importLines.empty"),
                      isCloseable: true,
                    });
                  }
                }
              },
            });
          });
          parsePromises.push(parsePromise);
        }
      });

      Promise.all(parsePromises)
        .then((parsedData) => {
          setUploadedRows(parsedData.flat());
        })
        .catch((error) => {
          console.error("Error parsing files:", error);
          enqueueToast({
            variant: "error",
            message: "Failed to parse uploaded files. Please try again.",
            isCloseable: true,
          });
        });
    }
  };

  const columnsHeaderSx = useMemo(
    () => ({
      fontWeight: theme.tokens.FuelFontWeightSemibold,
      fontSize: theme.tokens.FuelFontSizeHeading6,
    }),
    [theme]
  );

  const dataGridSx = useMemo(
    () => ({
      border: 1,
      borderColor: theme.tokens.FuelColorBorderLight,
      [`.${gridClasses.main}`]: {
        width: "50%",
      },
      [`.${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
        outline: "none",
      },
      [`.${gridClasses.cell}.${gridClasses["cell--editable"]}`]: {
        display: "flex",
        padding: "10px",
      },
      [`.${gridClasses.cell}.${gridClasses["cell--editing"]}`]: {
        boxShadow: "none !important",
        padding: "10px",
        outline: "none !important",
        backgroundColor: "none",
      },
      [`.${gridClasses.cell}.${gridClasses["cell--editing"]}:has(.Mui-focused)`]:
        {
          backgroundColor: `${theme.tokens.FuelColorBackgroundActionDefaultHovered}`,
        },
      [`.${gridClasses.row}.Mui-selected>.${gridClasses.cell}`]: {
        backgroundColor: `${theme.tokens.FuelColorBackgroundActionTertiaryHovered} !important`,
      },
      [`.${gridClasses.row}.Mui-selected:hover>.${gridClasses.cell}`]: {
        backgroundColor: `${theme.tokens.FuelColorBackgroundActionDefaultPressed} !important`,
      },
      [`& .${gridClasses.row}`]: {
        border: 1,
        borderLeft: 0,
        borderBottom: 0,
        borderColor: theme.tokens.FuelColorBorderLight,
      },
      [`& .${gridClasses.columnHeaders} > div[role="row"]`]: {
        backgroundColor: theme.tokens.FuelColorBackgroundWhite,
        borderColor: theme.tokens.FuelColorBorderLight,
      },
      [`& .${gridClasses.columnHeader}`]: {
        borderBottom: 0,
      },
      [`.${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
        {
          outline: "none",
        },
      [`.${gridClasses.columnSeparator}`]: {
        color: theme.tokens.FuelColorBorderLight,
      },
      [`.${gridClasses.virtualScroller}`]: {
        overflowY: "hidden",
      },
      [`.${gridClasses.checkboxInput}`]: {
        color: theme.tokens.FuelColorGray400,
      },
      [`.${gridClasses.cellCheckbox}>span`]: {
        marginTop: theme.tokens.FuelSpacing1,
        marginBottom: theme.tokens.FuelSpacing1,
      },
      [`.${gridClasses.cell}`]: {
        padding: "10px",
        alignItems: "flex-start",
      },
      [`.${gridClasses.filler}`]: {
        height: "0px !important",
      },
      [`.Mui-error`]: {
        borderColor: theme.tokens.FuelColorAlertErrorIcon,
      },
      "--unstable_DataGrid-radius": "8px",
    }),
    [theme, gridClasses]
  );

  const numberOfErrors = useMemo(
    () =>
      rows.filter(
        (row) =>
          (row.showPartNumberWarning && !row.showPartNumberInfo) ||
          row.showQuantityWarning
      ).length,
    [rows]
  );

  const [showRowsWithIssuesOnly, setShowRowsWithIssuesOnly] = useState(false);

  const visibleRows = useMemo(() => {
    if (!showRowsWithIssuesOnly) {
      return rows;
    }
    return rows.filter(
      (row) => row.showPartNumberWarning || row.showQuantityWarning
    );
  }, [rows, showRowsWithIssuesOnly]);

  const importLinesButtonCallback = () => {
    setShowFileUploadModal(true);
  };

  const handleProcessChanges = (checkModalWarning: boolean) => {
    const validatedRows = filterLinesWithSuccessfulValidation(rows);
    const enteredData = rows.filter((row) => row.partNumber && row.quantity);

    if (checkModalWarning) {
      const allRowsInvalid =
        validatedRows.length === 0 && enteredData.length > 0;
      const oneRowInvalid = rows.find(
        (row) =>
          (row.showPartNumberWarning && !row.showPartNumberInfo) ||
          row.showQuantityWarning
      );

      if (oneRowInvalid || allRowsInvalid) {
        setShowContinueToNextWarningModal(true);
        setNoValidLines(allRowsInvalid);
        return;
      }
    }

    processInventoryChanges();
  };

  const processInventoryChanges = () => {
    setIsProcessChangesButtonDisabled(true);

    const enteredData = rows.filter((row) => row.partNumber && row.quantity);

    console.log("Entered data:", enteredData);
    console.log("Total entered rows:", enteredData.length);

    if (enteredData.length === 0) {
      enqueueToast({
        variant: "warning",
        message: "No valid entries found to process.",
        isCloseable: true,
      });
      setIsProcessChangesButtonDisabled(false);
      return;
    }

    const inventoryPayload: D2DInventoryItem[] = enteredData.map((row) => ({
      partNumber: row.partNumber,
      quantity: row.quantity,
      action: false,
    }));

    const accessToken = getAccessToken();
    const accountId = getAccountId();
    const userId = getUserId();

    if (!accessToken || !accountId || !userId) {
      enqueueToast({
        variant: "error",
        message: "Missing authentication or account information.",
        isCloseable: true,
      });
      setIsProcessChangesButtonDisabled(false);
      return;
    }

    D2DInventoryApi.processInventory(
      D2D_INVENTORY_API_URL,
      inventoryPayload,
      accountId,
      userId,
      accessToken
    )
      .then((response) => {
        console.log("D2D Inventory API response:", response);

        // Store the counts for the success page
        const currentEnteredDataCount = rows.filter(
          (row) => row.partNumber && row.quantity
        ).length;

        setProcessedLineCount(currentEnteredDataCount);
        setProcessedFileCount(uploadedFileCount > 0 ? uploadedFileCount : 0);

        // Show success confirmation page instead of toast
        setShowSuccessConfirmation(true);
        onSuccessConfirmation?.(true);
      })
      .catch((error) => {
        console.error("Error processing D2D inventory:", error);
        enqueueToast({
          variant: "error",
          message: "Failed to process inventory. Please try again.",
          isCloseable: true,
        });
      })
      .finally(() => {
        setIsProcessChangesButtonDisabled(false);
      });
  };

  const cancelOrderButton = (
    <Button
      variant="text"
      sx={{ mr: theme.tokens.FuelSpacing2 }}
      data-testid="cancel-myLocatorEntry-button"
      onClick={() => setShowCancelOrderModal(true)}
    >
      {t("d2d:myLocatorEntry.cancel.button")}
    </Button>
  );

  const processChangesButton = (
    <Button
      variant="primary"
      disabled={isProcessChangesButtonDisabled}
      data-testid="process-changes"
      onClick={() => handleProcessChanges(true)}
    >
      {t("d2d:button.processChanges")}
    </Button>
  );

  // If success confirmation should be shown, render that instead
  if (showSuccessConfirmation) {
    return (
      <ProcessSuccess
        lineCount={processedLineCount}
        fileCount={processedFileCount > 0 ? processedFileCount : undefined}
      />
    );
  }

  return (
    <>
      {loadingIndicator && <LoadingIndicator overlayLoader />}

      {!(enteredRowCount() === numberOfErrors) &&
        showContinueToNextWarningModal && (
          <Dialog
            open={showContinueToNextWarningModal}
            onClose={() => {
              setShowContinueToNextWarningModal(false);
              setNoValidLines(false);
            }}
            aria-labelledby="warning-dialog-title"
            aria-describedby="warning-dialog-description"
          >
            <DialogTitle id="warning-dialog-title">
              {t("common:button.continueToNext")}
            </DialogTitle>
            <DialogContent>
              <Typography id="warning-dialog-description">
                {noValidLines
                  ? t("orders:modal.noValidLinesWarningOrderModal.description")
                  : t(
                      "orders:modal.continueToNextWarningOrderModal.description"
                    )}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setShowContinueToNextWarningModal(false);
                  setNoValidLines(false);
                }}
                variant="text"
              >
                {t("common:modal.cancelBtn")}
              </Button>
              <Button
                onClick={() => {
                  setShowContinueToNextWarningModal(false);
                  setNoValidLines(false);
                  handleProcessChanges(false);
                }}
                variant="primary"
                autoFocus
              >
                {noValidLines
                  ? t("orders:modal.noValidLinesWarningOrderModal.button.okay")
                  : t(
                      "orders:modal.continueToNextWarningOrderModal.button.continue"
                    )}
              </Button>
            </DialogActions>
          </Dialog>
        )}

      <D2DFileUploadModal
        openModal={showFileUploadModal}
        importLinesButtonCallback={(files) => {
          setShowFileUploadModal(false);
          setUploadedFileCount(files.length);
          parseFiles(files);
        }}
        closeModalCallback={() => {
          setShowFileUploadModal(false);
        }}
      />

      {numberOfErrors > 0 && (
        <Alert
          id="hasIssues-alert"
          data-testid="hasIssues-alert"
          variant="warning"
          sx={{
            marginBottom: "1em",
          }}
        >
          {t("d2d:myLocatorEntry.lines.hasIssues", {
            countIssues: numberOfErrors,
          })}
        </Alert>
      )}

      <DataGridPro
        columnBufferPx={columns.length + 1}
        rowBufferPx={100}
        data-testid="locator-data-grid-table"
        editMode="row"
        apiRef={apiRef}
        columns={columns}
        rows={visibleRows}
        processRowUpdate={processRowUpdate}
        getRowHeight={() => "auto"}
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnReorder
        hideFooter
        sx={dataGridSx}
        slots={{
          toolbar: () => (
            <EntryLinesToolbar
              {...{
                viewIssuesOnly: showRowsWithIssuesOnly,
                changeViewIssuesOnly: setShowRowsWithIssuesOnly,
                selectedRow,
                resetButtonCallback,
                isResetButtonDisabled,
                isViewErrorsEnabled: numberOfErrors > 0,
                onViewErrorsChange: (checked: boolean) =>
                  setShowRowsWithIssuesOnly(checked),
                importLinesButtonCallback,
                isMaxOrderLinesReached: enteredRowCount() >= MAX_ORDER_LINES,
                addRows,
              }}
            />
          ),
        }}
        slotProps={{}}
        onRowSelectionModelChange={(ids) => onRowsSelectionHandler(ids)}
        rowSelectionModel={selectedRow
          .filter((r) => r)
          .map((r: GridRowModel) => r.id)}
        pagination
        onStateChange={(state) => {
          const parentRowCount = gridFilteredTopLevelRowCountSelector(
            state,
            apiRef.current.instanceId
          );
          setUniqueRows(parentRowCount);
        }}
      />

      <StickyFooter
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <OrderLinesPagination
          page={page + 1}
          pageSize={pageSize}
          rowLength={uniqueRows}
          setPage={setPage}
          setPageSize={setPageSize}
          api={apiRef.current}
        />
        <Box sx={{ display: "flex", gap: theme.tokens.FuelSpacing2 }}>
          {cancelOrderButton}
          {processChangesButton}
        </Box>
      </StickyFooter>
    </>
  );
};

export default EntryLines;
